import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'ink';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  primary:
    'bg-makhe-brand text-makhe-on shadow-ink hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0',
  ink:
    'bg-makhe-ink text-makhe-bg hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0',
  secondary:
    'bg-transparent text-makhe-ink border border-makhe-ink hover:bg-makhe-ink hover:text-makhe-bg disabled:opacity-50',
  ghost:
    'bg-transparent text-makhe-ink hover:bg-makhe-ink/5 disabled:opacity-50',
  danger:
    'bg-makhe-plum text-white hover:-translate-y-0.5 disabled:opacity-50',
};

const sizes: Record<Size, string> = {
  sm: 'px-4 py-1.5 text-[11px]',
  md: 'px-6 py-3 text-xs',
  lg: 'px-8 py-4 text-sm',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, className, children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-full font-medium uppercase tracking-widest transition-all duration-200 disabled:cursor-not-allowed disabled:translate-y-0',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? <span className="animate-pulse">…</span> : children}
    </button>
  ),
);
Button.displayName = 'Button';
